import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { api, getToken, assetUrl } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft, Plus, Search, MapPin, Eye, Phone, MessageCircle, Trash2, Flag, X, Image as ImageIcon, Upload, AlertTriangle, Navigation, ExternalLink } from "lucide-react";
import CitySelector from "../components/CitySelector";
import AdShareButtons from "../components/AdShareButtons";
import { RADIUS_OPTIONS, findCityCoords } from "../data/madaCities";

const fmtPrice = (ad) => {
  if (ad.price_type === "free") return "Gratuit";
  if (ad.price_type === "exchange") return "Échange";
  if (ad.price_type === "negotiable") return "À débattre";
  if (ad.price_amount) return new Intl.NumberFormat("fr-FR").format(ad.price_amount) + " " + (ad.currency || "Ar");
  return "—";
};
const timeAgo = (iso) => {
  try {
    const d = new Date((iso || "").replace(" ", "T") + (iso?.endsWith("Z") ? "" : "Z"));
    const diff = Math.floor((Date.now() - d) / 1000);
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff/60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff/3600)} h`;
    return `il y a ${Math.floor(diff/86400)} j`;
  } catch { return ""; }
};

// Composant photo upload pour le formulaire annonce
const PhotoUploader = ({ photos, onChange, max = 5 }) => {
  const [uploading, setUploading] = useState(false);
  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const remaining = max - photos.length;
    if (remaining <= 0) { toast.error(`Max ${max} photos`); return; }
    setUploading(true);
    try {
      const out = [];
      for (const f of files.slice(0, remaining)) {
        const fd = new FormData(); fd.append("file", f); fd.append("kind", "arnak");
        const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
        out.push(data.url);
      }
      onChange([...photos, ...out]);
      toast.success(`${out.length} photo(s) ajoutée(s)`);
    } catch (err) { toast.error("Erreur upload"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-2">
        {photos.map((p, i) => (
          <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-white/[0.04] border border-white/10">
            <img src={assetUrl(p)} alt="" className="w-full h-full object-cover" />
            <button type="button" onClick={() => onChange(photos.filter((_, j) => j !== i))} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 hover:bg-red-500 text-white flex items-center justify-center transition" data-testid={`ad-photo-remove-${i}`}>
              <X size={13} />
            </button>
          </div>
        ))}
        {photos.length < max && (
          <label className="aspect-square rounded-lg border-2 border-dashed border-yellow-400/30 hover:border-yellow-400/70 bg-yellow-400/[0.04] flex flex-col items-center justify-center cursor-pointer transition">
            {uploading ? (
              <span className="text-xs text-yellow-300">…</span>
            ) : (
              <>
                <Upload size={20} className="text-yellow-400 mb-1" />
                <span className="text-[10px] text-white/55 font-bold">Ajouter</span>
              </>
            )}
            <input type="file" accept="image/*" multiple onChange={handle} className="hidden" disabled={uploading} />
          </label>
        )}
      </div>
      <p className="text-[11px] text-white/45">{photos.length}/{max} photos · JPG/PNG/WEBP · max 5 Mo chacune</p>
    </div>
  );
};

// ─── LISTE DES ANNONCES ───
export const ArnakAdsListPage = () => {
  const [ads, setAds] = useState([]);
  const [banners, setBanners] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [city, setCity] = useState(null); // { name, lat, lng } | null
  const [radius, setRadius] = useState(25); // km
  const [useGeo, setUseGeo] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);
  const [userPos, setUserPos] = useState(null); // { lat, lng }
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const isArnakDomain = typeof window !== "undefined" && /arnakclan\.com$/i.test(window.location.hostname);
  const backHref = isArnakDomain ? "/" : "/arnak";

  useEffect(() => { document.title = "Annonces — ARNAK"; }, []);
  useEffect(() => { api.get("/arnak/ads/categories").then(({ data }) => setCats(data.categories || [])); }, []);
  useEffect(() => {
    api.get("/arnak/ad-banners")
      .then(({ data }) => setBanners(data.banners || []))
      .catch(() => setBanners([]));
  }, []);

  const requestGeolocation = () => {
    if (!navigator.geolocation) {
      toast.error("Ton navigateur ne supporte pas la géolocalisation");
      return;
    }
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setUseGeo(true);
        setGeoLoading(false);
        toast.success("📍 Position détectée");
      },
      (err) => {
        setGeoLoading(false);
        if (err.code === 1) toast.error("Autorisation refusée — autorise la géoloc dans ton navigateur");
        else toast.error("Impossible de récupérer ta position");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const clearGeo = () => {
    setUseGeo(false);
    setUserPos(null);
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (cat) params.set("category", cat);
      // Coordonnées d'origine : GPS user > ville sélectionnée avec coords
      let origin = null;
      if (useGeo && userPos) origin = userPos;
      else if (city?.lat != null && city?.lng != null) origin = { lat: city.lat, lng: city.lng };
      else if (city?.name) {
        const found = findCityCoords(city.name);
        if (found) origin = { lat: found.lat, lng: found.lng };
      }
      if (origin) {
        params.set("lat", origin.lat);
        params.set("lng", origin.lng);
        params.set("radius_km", radius);
      } else if (city?.name) {
        params.set("city", city.name);
      }
      const { data } = await api.get("/arnak/ads?" + params.toString());
      setAds(data.ads || []);
    } catch (e) { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const onSearch = (e) => { e.preventDefault(); load(); };
  // Recharge auto quand on change rayon/GPS/ville (debounced via dépendance simple)
  useEffect(() => { if (useGeo || city) load(); /* eslint-disable-next-line */ }, [radius, useGeo, userPos]);

  // Construit la grille en intercalant les bannières toutes les 6 annonces
  const buildGrid = () => {
    const out = [];
    let bannerIndex = 0;
    ads.forEach((ad, i) => {
      out.push({ type: "ad", data: ad });
      // Toutes les 6 annonces, on insère une bannière (si dispo)
      if ((i + 1) % 6 === 0 && banners.length > 0) {
        out.push({ type: "banner", data: banners[bannerIndex % banners.length] });
        bannerIndex++;
      }
    });
    return out;
  };

  const grid = buildGrid();
  const showGeoActive = useGeo && userPos;
  const showCityActive = !useGeo && city?.lat != null && city?.lng != null;

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="ads-list-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-xl border-b border-yellow-400/20 px-4 md:px-8 py-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Link to={backHref} className="p-2 -ml-1 rounded-lg hover:bg-yellow-400/15 hover:text-yellow-400 text-white/85 transition shrink-0" data-testid="ads-back">
            <ArrowLeft size={22} />
          </Link>
          <div className="min-w-0">
            <p className="font-black text-xl md:text-2xl tracking-tight truncate" style={{ fontFamily: "'Anton', sans-serif" }}>
              <span className="text-yellow-400">ANNONCES</span> ARNAK
            </p>
            <p className="text-[10px] uppercase tracking-widest text-white/55 hidden sm:block">Marketplace de la communauté</p>
          </div>
        </div>
        <button
          onClick={() => navigate("/annonces/nouvelle")}
          className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-black uppercase tracking-widest transition shadow-lg shadow-yellow-400/20"
          data-testid="ads-create-btn"
        >
          <Plus size={14} /> <span className="hidden sm:inline">Publier</span>
        </button>
      </header>

      {/* Filtres améliorés */}
      <form onSubmit={onSearch} className="max-w-6xl mx-auto px-4 md:px-8 py-5 border-b border-white/5 space-y-3">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/35 z-10" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher (titre, mot-clé…)" className="w-full pl-9 pr-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-yellow-400 focus:outline-none" data-testid="ads-search-input" />
          </div>
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg text-sm focus:border-yellow-400 focus:outline-none" data-testid="ads-cat-filter">
            <option value="">Toutes catégories</option>
            {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          <CitySelector value={city} onChange={(c) => { setCity(c); if (c) setUseGeo(false); }} placeholder="Toutes villes" testId="ads-city-selector" />
          <button type="submit" className="px-4 py-2.5 bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-black uppercase tracking-widest rounded-lg transition" data-testid="ads-filter-submit">
            Filtrer
          </button>
        </div>

        {/* Geo + Rayon */}
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            onClick={showGeoActive ? clearGeo : requestGeolocation}
            disabled={geoLoading}
            className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-black uppercase tracking-widest transition border ${showGeoActive ? "bg-yellow-400 text-black border-yellow-400" : "bg-white/5 hover:bg-yellow-400/10 hover:text-yellow-300 text-white/75 border-white/10 hover:border-yellow-400/40"}`}
            data-testid="ads-geo-toggle"
          >
            <Navigation size={13} />
            {geoLoading ? "Localisation…" : showGeoActive ? "📍 Autour de moi (actif)" : "📍 Autour de moi"}
          </button>
          {(showGeoActive || showCityActive) && (
            <div className="inline-flex items-center gap-2 bg-white/5 border border-white/10 rounded-full pl-3.5 pr-1.5 py-1.5">
              <span className="text-[10px] uppercase tracking-widest text-white/55 font-bold">Rayon</span>
              <select value={radius} onChange={(e) => setRadius(parseInt(e.target.value, 10))} className="bg-transparent border-0 text-xs font-black text-yellow-300 focus:outline-none cursor-pointer pr-2" data-testid="ads-radius-select">
                {RADIUS_OPTIONS.map((r) => <option key={r.value} value={r.value} className="bg-black">{r.label}</option>)}
              </select>
            </div>
          )}
          {showGeoActive && (
            <span className="text-[10px] text-white/45">
              Lat {userPos.lat.toFixed(3)}, Lng {userPos.lng.toFixed(3)}
            </span>
          )}
          {!showGeoActive && city?.name && city?.lat == null && (
            <span className="text-[10px] text-orange-300/80 inline-flex items-center gap-1">
              <AlertTriangle size={11} /> Ville "{city.name}" sans GPS — filtre texte uniquement
            </span>
          )}
        </div>
      </form>

      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 pb-20">
        {/* Bandeau CTA "Annonces d'exemple" */}
        {ads.some((a) => (a.ad_id || "").startsWith("ad_seed_")) && (
          <div className="mb-6 relative overflow-hidden rounded-2xl bg-gradient-to-r from-yellow-400 via-yellow-300 to-amber-300 p-5 sm:p-6" data-testid="ads-demo-banner">
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-black/10 blur-2xl pointer-events-none" />
            <div className="absolute -left-6 -bottom-10 w-32 h-32 rounded-full bg-white/15 blur-xl pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-black/60 mb-1">
                  Aperçu de la marketplace
                </p>
                <p className="font-black text-black text-2xl sm:text-3xl leading-tight" style={{ fontFamily: "'Anton', sans-serif" }}>
                  Les annonces ci-dessous sont des <span className="underline decoration-2 decoration-black/60">EXEMPLES</span>.
                </p>
                <p className="font-black text-black text-xl sm:text-2xl leading-tight" style={{ fontFamily: "'Anton', sans-serif" }}>
                  👉 Maintenant c'est à <span className="text-red-700">VOUS</span> de poster !
                </p>
                <p className="text-black/70 text-sm mt-1">
                  Ajoute ta première annonce et la communauté ARNAK la verra dans toute Madagascar 🇲🇬
                </p>
              </div>
              <button
                onClick={() => navigate("/annonces/nouvelle")}
                className="shrink-0 inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-black text-yellow-400 hover:bg-zinc-900 hover:scale-105 text-sm font-black uppercase tracking-widest transition shadow-xl shadow-black/40"
                data-testid="ads-demo-banner-cta"
              >
                <Plus size={16} /> Publier mon annonce
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-center py-12 text-white/40">Chargement…</p>
        ) : ads.length === 0 ? (
          <div className="text-center py-16">
            <ImageIcon size={36} className="mx-auto text-white/15 mb-3" />
            <p className="text-white/55 text-sm font-bold">Aucune annonce trouvée</p>
            {(showGeoActive || showCityActive) && (
              <p className="text-xs text-white/40 mt-1">Essaie d'agrandir le rayon ou de changer de ville.</p>
            )}
            <button onClick={() => navigate("/annonces/nouvelle")} className="mt-5 inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-black uppercase tracking-widest transition">
              <Plus size={14} /> Publier la première
            </button>
          </div>
        ) : (
          <>
            <p className="text-[11px] uppercase tracking-widest text-white/45 font-bold mb-3">
              {ads.length} annonce{ads.length > 1 ? "s" : ""} {(showGeoActive || showCityActive) && <>· dans un rayon de <span className="text-yellow-300">{radius} km</span></>}
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {grid.map((cell, idx) => cell.type === "banner" ? (
                <BannerCard key={`banner-${idx}`} banner={cell.data} />
              ) : (
                <AdCard key={cell.data.ad_id} ad={cell.data} cats={cats} />
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
};

// Fallback déterministe si une URL externe (LoremFlickr) ne répond pas
const photoFallback = (adId, idx = 0) => `https://picsum.photos/seed/arnak-${adId}-${idx}/800/600`;
const onImgError = (e, adId, idx = 0) => {
  if (!e.currentTarget.dataset.fallback) {
    e.currentTarget.dataset.fallback = "1";
    e.currentTarget.src = photoFallback(adId, idx);
  }
};

// ─── Carte annonce ───
const AdCard = ({ ad, cats }) => {
  const isExample = (ad.ad_id || "").startsWith("ad_seed_");
  return (
  <Link to={`/annonces/${ad.ad_id}`} className="bg-white/[0.03] hover:bg-white/[0.06] border border-white/10 hover:border-yellow-400/40 rounded-2xl overflow-hidden transition group" data-testid={`ad-card-${ad.ad_id}`}>
    <div className="aspect-[4/3] bg-zinc-900 relative">
      {ad.photos?.[0] ? (
        <img
          src={assetUrl(ad.photos[0])}
          alt={ad.title}
          loading="lazy"
          onError={(e) => onImgError(e, ad.ad_id, 0)}
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-white/20"><ImageIcon size={40} /></div>
      )}

      {/* Watermark EXEMPLE en diagonale pour les annonces de démo */}
      {isExample && (
        <>
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden">
            <span
              className="font-black text-white/30 tracking-[0.3em] select-none"
              style={{
                transform: "rotate(-22deg)",
                fontFamily: "'Anton', sans-serif",
                fontSize: "clamp(28px, 5.5vw, 56px)",
                textShadow: "0 2px 12px rgba(0,0,0,0.6)",
              }}
            >
              EXEMPLE
            </span>
          </div>
          <div className="absolute top-2 right-2 px-2 py-0.5 bg-red-500 text-white text-[9px] font-black uppercase tracking-widest rounded shadow-lg">
            Démo
          </div>
        </>
      )}

      <div className={`absolute top-2 left-2 px-2.5 py-1 bg-black/65 backdrop-blur text-[10px] font-black uppercase tracking-widest rounded`}>
        {cats.find((c) => c.key === ad.category)?.label?.split(" ")[0] || ad.category}
      </div>
      {ad.distance_km != null && !isExample && (
        <div className="absolute top-2 right-2 px-2.5 py-1 bg-yellow-400 text-black text-[10px] font-black rounded inline-flex items-center gap-1">
          <Navigation size={10} /> {ad.distance_km} km
        </div>
      )}
    </div>
    <div className="p-4">
      <p className="font-black text-yellow-400 text-lg mb-1 group-hover:text-yellow-300 transition" style={{ fontFamily: "'Anton', sans-serif" }}>{fmtPrice(ad)}</p>
      <p className="font-bold text-sm leading-tight line-clamp-2 mb-1.5">{ad.title}</p>
      <p className="text-[11px] text-white/45 flex items-center gap-1.5">
        {ad.city && <><MapPin size={11} /> {ad.city}</>}
        {ad.city && <span>·</span>}
        {timeAgo(ad.created_at)}
      </p>
    </div>
  </Link>
);
};

// ─── Carte bannière partenaire ───
const BannerCard = ({ banner }) => {
  const inner = (
    <div className="relative h-full bg-gradient-to-br from-yellow-400/10 via-yellow-400/5 to-transparent border border-yellow-400/30 hover:border-yellow-400/60 rounded-2xl overflow-hidden transition group">
      <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/70 backdrop-blur text-[9px] font-black uppercase tracking-widest text-yellow-300 rounded z-10">
        Sponsor
      </div>
      <img src={assetUrl(banner.image_url)} alt={banner.title} className="w-full h-full object-cover" />
      <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 to-transparent">
        <p className="font-black text-white truncate">{banner.title}</p>
        {banner.link_url && (
          <p className="text-[10px] text-yellow-300 inline-flex items-center gap-1 mt-0.5">
            <ExternalLink size={10} /> En savoir plus
          </p>
        )}
      </div>
    </div>
  );
  if (banner.link_url) {
    return (
      <a href={banner.link_url} target="_blank" rel="noopener noreferrer sponsored" className="block min-h-[280px]" data-testid={`banner-${banner.banner_id}`}>
        {inner}
      </a>
    );
  }
  return <div className="min-h-[280px]" data-testid={`banner-${banner.banner_id}`}>{inner}</div>;
};

// ─── DÉTAIL ───
export const ArnakAdDetailPage = () => {
  const { id } = useParams();
  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [cats, setCats] = useState([]);
  const [activePhoto, setActivePhoto] = useState(0);
  const navigate = useNavigate();
  const isArnakDomain = typeof window !== "undefined" && /arnakclan\.com$/i.test(window.location.hostname);

  useEffect(() => {
    api.get(`/arnak/ads/${id}`).then(({ data }) => {
      setAd(data.ad);
      document.title = data.ad?.title ? `${data.ad.title} — ARNAK Annonces` : "Annonce";
    }).catch(() => { toast.error("Annonce introuvable"); navigate("/annonces"); })
      .finally(() => setLoading(false));
    if (getToken()) api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => {});
    api.get("/arnak/ads/categories").then(({ data }) => setCats(data.categories || []));
  }, [id, navigate]);

  if (loading) return <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center text-sm">Chargement…</div>;
  if (!ad) return null;

  const isOwner = me && me.user_id === ad.user_id;
  const canModerate = me && (me.is_admin || me.is_arnak_admin);
  const catLabel = cats.find((c) => c.key === ad.category)?.label || ad.category;

  const onDelete = async () => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try { await api.post(`/arnak/ads/${id}/delete`); toast.success("Supprimée"); navigate("/annonces"); }
    catch (e) { toast.error("Erreur"); }
  };
  const onReport = async () => {
    const reason = window.prompt("Pourquoi signaler cette annonce ?\n(arnaque, contenu inapproprié, faux produit, etc.)");
    if (!reason) return;
    try { await api.post(`/arnak/ads/${id}/report`, { reason }); toast.success("Merci, signalement envoyé"); }
    catch (e) { toast.error("Erreur"); }
  };

  const dmHref = (isArnakDomain ? "/messages" : "/arnak/messages") + "?with=" + encodeURIComponent(ad.user_id);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white" data-testid="ad-detail-page">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-xl border-b border-yellow-400/20 px-4 md:px-8 py-3 flex items-center gap-3">
        <Link to="/annonces" className="p-2 -ml-1 rounded-lg hover:bg-yellow-400/15 hover:text-yellow-400 text-white/85 transition" data-testid="ad-detail-back">
          <ArrowLeft size={22} />
        </Link>
        <p className="font-bold truncate flex-1">Annonce</p>
        {!isOwner && me && (
          <button onClick={onReport} className="p-2 rounded-lg hover:bg-red-500/15 hover:text-red-300 text-white/55 transition" title="Signaler" data-testid="ad-report-btn">
            <Flag size={16} />
          </button>
        )}
        {(isOwner || canModerate) && (
          <button onClick={onDelete} className="p-2 rounded-lg hover:bg-red-500/15 hover:text-red-300 text-white/55 transition" title="Supprimer" data-testid="ad-delete-btn">
            <Trash2 size={16} />
          </button>
        )}
      </header>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-6 pb-24">
        {ad.status === "pending" && (
          <div className="mb-5 p-3 bg-orange-500/15 border border-orange-400/30 rounded-lg flex items-start gap-2.5">
            <AlertTriangle size={18} className="text-orange-300 shrink-0 mt-0.5" />
            <p className="text-sm text-orange-100">Cette annonce est <strong>en attente de modération</strong> et n'est pas visible publiquement.</p>
          </div>
        )}

        {/* Photos */}
        {ad.photos?.length > 0 ? (
          <div className="mb-6">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-zinc-900 mb-3 relative">
              <img
                src={assetUrl(ad.photos[activePhoto])}
                alt={ad.title}
                onError={(e) => {
                  if (!e.currentTarget.dataset.fallback) {
                    e.currentTarget.dataset.fallback = "1";
                    e.currentTarget.src = `https://picsum.photos/seed/arnak-${ad.ad_id}-${activePhoto}/800/600`;
                  }
                }}
                className="w-full h-full object-cover"
              />
              {/* Watermark EXEMPLE sur photo détail des annonces démo */}
              {(ad.ad_id || "").startsWith("ad_seed_") && (
                <>
                  <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden">
                    <span
                      className="font-black text-white/40 tracking-[0.4em] select-none"
                      style={{
                        transform: "rotate(-18deg)",
                        fontFamily: "'Anton', sans-serif",
                        fontSize: "clamp(48px, 9vw, 110px)",
                        textShadow: "0 4px 24px rgba(0,0,0,0.7)",
                      }}
                    >
                      EXEMPLE
                    </span>
                  </div>
                  <div className="absolute top-3 right-3 px-3 py-1 bg-red-500 text-white text-[10px] font-black uppercase tracking-widest rounded shadow-lg">
                    Annonce démo
                  </div>
                </>
              )}
            </div>
            {ad.photos.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {ad.photos.map((p, i) => (
                  <button key={i} onClick={() => setActivePhoto(i)} className={`w-20 h-20 rounded-lg overflow-hidden shrink-0 border-2 transition ${activePhoto === i ? "border-yellow-400" : "border-white/10 hover:border-white/30"}`}>
                    <img
                      src={assetUrl(p)}
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        if (!e.currentTarget.dataset.fallback) {
                          e.currentTarget.dataset.fallback = "1";
                          e.currentTarget.src = `https://picsum.photos/seed/arnak-${ad.ad_id}-${i}/200/200`;
                        }
                      }}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="aspect-[4/3] rounded-2xl bg-zinc-900 mb-6 flex items-center justify-center text-white/20"><ImageIcon size={64} /></div>
        )}

        {/* Infos */}
        <div className="flex items-start justify-between gap-3 mb-2">
          <span className="inline-block px-3 py-1 bg-yellow-400/15 text-yellow-300 text-[10px] uppercase tracking-widest font-bold rounded-full">{catLabel}</span>
          <p className="text-xs text-white/45 flex items-center gap-1.5"><Eye size={11} /> {ad.views} vues</p>
        </div>
        {/* Bandeau EXEMPLE pour les annonces de démo */}
        {(ad.ad_id || "").startsWith("ad_seed_") && (
          <div className="mb-5 relative overflow-hidden rounded-2xl bg-gradient-to-r from-yellow-400 via-yellow-300 to-amber-300 p-5 sm:p-6" data-testid="ad-detail-demo-banner">
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-black/10 blur-2xl pointer-events-none" />
            <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
              <div className="flex-1 min-w-0">
                <p className="text-[10px] font-black uppercase tracking-widest text-black/60 mb-1">
                  ⚠️ Annonce d'exemple
                </p>
                <p className="font-black text-black text-xl sm:text-2xl leading-tight" style={{ fontFamily: "'Anton', sans-serif" }}>
                  Maintenant c'est à <span className="text-red-700">VOUS</span> de poster !
                </p>
                <p className="text-black/70 text-xs mt-1">
                  Cette annonce est une démo. Publie la tienne en 2 minutes — gratuit, visible par toute la communauté ARNAK 🇲🇬
                </p>
              </div>
              <Link
                to="/annonces/nouvelle"
                className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-black text-yellow-400 hover:bg-zinc-900 hover:scale-105 text-xs font-black uppercase tracking-widest transition shadow-xl shadow-black/40"
                data-testid="ad-detail-demo-cta"
              >
                <Plus size={14} /> Publier la mienne
              </Link>
            </div>
          </div>
        )}

        <h1 className="font-black text-3xl md:text-4xl tracking-tight mb-3" style={{ fontFamily: "'Anton', sans-serif" }}>{ad.title}</h1>
        <p className="font-black text-3xl text-yellow-400 mb-4">{fmtPrice(ad)}</p>
        {ad.city && (
          <p className="text-white/65 text-sm mb-3 inline-flex items-center gap-1.5"><MapPin size={14} className="text-yellow-400" /> {ad.city}</p>
        )}

        <div className="mb-5">
          <AdShareButtons
            url={typeof window !== "undefined" ? window.location.href : ""}
            title={`${ad.title} — ${fmtPrice(ad)}`}
            text={`${ad.title}${ad.city ? " · " + ad.city : ""} · ${fmtPrice(ad)}`}
          />
        </div>

        <div className="whitespace-pre-wrap text-white/85 text-base leading-relaxed mb-8 bg-white/[0.02] border border-white/5 rounded-lg p-4">
          {ad.description}
        </div>

        {/* Author + Actions */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 mb-6">
          <div className="flex items-center gap-3 mb-4">
            {ad.author_picture ? (
              <img src={assetUrl(ad.author_picture)} alt={ad.author_name} className="w-12 h-12 rounded-full object-cover" />
            ) : (
              <div className="w-12 h-12 rounded-full bg-yellow-400/15 text-yellow-300 flex items-center justify-center font-black">{(ad.author_name || "M").slice(0, 2).toUpperCase()}</div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-bold truncate">{ad.author_name || "Membre"}</p>
              <p className="text-[11px] text-white/45">Publiée {timeAgo(ad.created_at)}</p>
            </div>
          </div>
          {!isOwner && (
            <div className="flex flex-col sm:flex-row gap-2">
              {me ? (
                <Link to={dmHref} className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-black uppercase tracking-widest transition" data-testid="ad-contact-btn">
                  <MessageCircle size={16} /> Envoyer un message
                </Link>
              ) : (
                <Link to="/login?next=/annonces" className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-yellow-400 hover:bg-yellow-300 text-black text-sm font-black uppercase tracking-widest transition">
                  <MessageCircle size={16} /> Se connecter pour contacter
                </Link>
              )}
              {ad.phone && (
                <a href={`tel:${ad.phone}`} className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-lg bg-white/10 hover:bg-white/20 text-sm font-black uppercase tracking-widest transition" data-testid="ad-phone-btn">
                  <Phone size={16} /> {ad.phone}
                </a>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// ─── CRÉATION ───
export const ArnakAdNewPage = () => {
  const [cats, setCats] = useState([]);
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", category: "", price_type: "fixed", price_amount: "",
    condition_status: "", city: "", lat: null, lng: null, phone: "", photos: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Publier une annonce — ARNAK";
    if (!getToken()) { navigate("/login?next=/annonces/nouvelle"); return; }
    api.get("/auth/me").then(({ data }) => setMe(data)).catch(() => navigate("/login"));
    api.get("/arnak/ads/categories").then(({ data }) => setCats(data.categories || []));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category) { toast.error("Choisis une catégorie"); return; }
    setSubmitting(true);
    try {
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
        price_type: form.price_type,
        price_amount: form.price_type === "fixed" ? (parseInt(form.price_amount, 10) || 0) : null,
        condition_status: form.condition_status || null,
        city: form.city.trim() || null,
        lat: form.lat ?? null,
        lng: form.lng ?? null,
        phone: form.phone.trim() || null,
        photos: form.photos,
      };
      const { data } = await api.post("/arnak/ads", payload);
      setDone(data);
    } catch (err) { toast.error(err?.response?.data?.error || "Erreur"); }
    finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center px-5">
        <div className="max-w-md text-center">
          {done.pending_review ? (
            <>
              <AlertTriangle size={48} className="mx-auto text-orange-300 mb-4" />
              <h1 className="font-black text-2xl mb-3" style={{ fontFamily: "'Anton', sans-serif" }}>En attente de modération</h1>
              <p className="text-white/65 text-sm mb-6">{done.message}</p>
            </>
          ) : (
            <>
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-yellow-400/15 flex items-center justify-center text-3xl">✅</div>
              <h1 className="font-black text-3xl mb-3" style={{ fontFamily: "'Anton', sans-serif" }}>Annonce publiée !</h1>
              <p className="text-white/65 text-sm mb-6">Elle est visible immédiatement par tous les membres ARNAK.</p>
            </>
          )}
          <div className="flex gap-2 justify-center">
            <Link to={`/annonces/${done.ad?.ad_id}`} className="px-5 py-3 rounded-full bg-yellow-400 hover:bg-yellow-300 text-black text-xs font-black uppercase tracking-widest transition">Voir mon annonce</Link>
            <Link to="/annonces" className="px-5 py-3 rounded-full bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-widest transition">← Retour</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <header className="sticky top-0 z-30 bg-black/85 backdrop-blur-xl border-b border-yellow-400/20 px-4 md:px-8 py-3 flex items-center gap-3">
        <Link to="/annonces" className="p-2 -ml-1 rounded-lg hover:bg-yellow-400/15 hover:text-yellow-400 text-white/85 transition">
          <ArrowLeft size={22} />
        </Link>
        <p className="font-black text-xl" style={{ fontFamily: "'Anton', sans-serif" }}>Publier une <span className="text-yellow-400">annonce</span></p>
      </header>
      <form onSubmit={submit} className="max-w-2xl mx-auto px-4 md:px-8 py-6 pb-24 space-y-4" data-testid="ad-new-form">
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1.5">Catégorie *</label>
          <select required value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg focus:border-yellow-400 focus:outline-none" data-testid="ad-category">
            <option value="">Choisis…</option>
            {cats.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1.5">Titre *</label>
          <input required minLength={4} maxLength={120} value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} placeholder="Ex: Vends micro Shure SM58 quasi neuf" className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg focus:border-yellow-400 focus:outline-none" data-testid="ad-title" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1.5">Description *</label>
          <textarea required minLength={10} maxLength={5000} rows={5} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} placeholder="Décris l'objet/service en détail…" className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg focus:border-yellow-400 focus:outline-none resize-none" data-testid="ad-desc" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1.5">Photos (max 5)</label>
          <PhotoUploader photos={form.photos} onChange={(photos) => setForm({...form, photos})} max={5} />
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1.5">Type de prix</label>
            <select value={form.price_type} onChange={(e) => setForm({...form, price_type: e.target.value})} className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg focus:border-yellow-400 focus:outline-none" data-testid="ad-price-type">
              <option value="fixed">Prix fixe (Ar)</option>
              <option value="negotiable">À débattre</option>
              <option value="free">Gratuit</option>
              <option value="exchange">Échange</option>
            </select>
          </div>
          {form.price_type === "fixed" && (
            <div>
              <label className="block text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1.5">Prix (Ariary) *</label>
              <input required type="number" min="0" value={form.price_amount} onChange={(e) => setForm({...form, price_amount: e.target.value})} placeholder="ex: 250000" className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg focus:border-yellow-400 focus:outline-none" data-testid="ad-price" />
            </div>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1.5">État</label>
            <select value={form.condition_status} onChange={(e) => setForm({...form, condition_status: e.target.value})} className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg focus:border-yellow-400 focus:outline-none">
              <option value="">—</option>
              <option value="new">Neuf</option>
              <option value="good">Très bon état</option>
              <option value="used">Occasion</option>
              <option value="damaged">Pour pièces / abîmé</option>
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1.5">Ville</label>
            <CitySelector
              value={form.city ? { name: form.city } : null}
              onChange={(c) => setForm({ ...form, city: c?.name || "", lat: c?.lat ?? null, lng: c?.lng ?? null })}
              placeholder="Choisis ta ville…"
              testId="ad-city-selector"
            />
            <p className="text-[10px] text-white/45 mt-1">Permet aux acheteurs proches de te trouver via le filtre "Autour de moi".</p>
          </div>
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-1.5">Téléphone (optionnel)</label>
          <input value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} placeholder="+261…" className="w-full px-3 py-2.5 bg-white/[0.04] border border-white/10 rounded-lg focus:border-yellow-400 focus:outline-none" />
          <p className="text-[10px] text-white/45 mt-1">Optionnel. Sans ça, on te contacte uniquement via message privé ARNAK.</p>
        </div>
        <div className="pt-2 flex flex-col sm:flex-row gap-2">
          <button type="submit" disabled={submitting} className="flex-1 py-3.5 rounded-full bg-yellow-400 hover:bg-yellow-300 disabled:opacity-40 text-black text-sm font-black uppercase tracking-widest transition" data-testid="ad-submit-btn">
            {submitting ? "Publication…" : "Publier mon annonce"}
          </button>
          <Link to="/annonces" className="py-3.5 px-6 text-center rounded-full bg-white/10 hover:bg-white/20 text-sm font-black uppercase tracking-widest transition">Annuler</Link>
        </div>
        <p className="text-[10px] text-white/45 text-center pt-2">
          Tu reçois les contacts via la messagerie privée ARNAK. Toute annonce trompeuse, illégale ou abusive sera supprimée immédiatement.
        </p>
      </form>
    </div>
  );
};
